import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "./queryKeys";

export interface Tenant {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  unitAlias?: string;
  unitNumber?: string;
  roomAlias?: string;
  roomNumber?: string;
}

export interface Unit {
  id: string;
  unitId: string;
  alias: string;
  unitNumber: string;
  unitType: string;
  maxTenants: number;
  rentAmount: number;
  dueDate: number;
  tenants: Tenant[];
}

export interface Property {
  id: string;
  name: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  totalRent: number;
  isArchived: boolean;
  createdAt: string;
  units: Unit[];
}

export interface RentRecord {
  id: string;
  roomId: string;
  propertyName: string;
  roomAlias: string;
  roomNumber: string;
  month: number;
  year: number;
  amount: number;
  status: string;
  paidDate: string | null;
  paymentMode: string | null;
  notes: string | null;
}

async function fetchProperties() {
  const res = await api.get<{ properties: Property[] }>("/properties");
  return res.properties;
}

async function fetchRentRecords(year: number, month?: number) {
  const params = new URLSearchParams({ year: String(year) });
  if (month != null) params.set("month", String(month));
  const res = await api.get<{ records: RentRecord[] }>(`/rent?${params}`);
  return res.records;
}

export function usePropertiesQuery() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  const propertiesQuery = useQuery({
    queryKey: queryKeys.properties(),
    queryFn: fetchProperties,
  });

  const rentQuery = useQuery({
    queryKey: queryKeys.rentRecords({ year, month }),
    queryFn: () => fetchRentRecords(year, month),
  });

  return {
    properties: propertiesQuery.data ?? [],
    rentRecords: rentQuery.data ?? [],
    loading: propertiesQuery.isLoading || rentQuery.isLoading,
    error: propertiesQuery.error ?? rentQuery.error,
    refetch: () => {
      propertiesQuery.refetch();
      rentQuery.refetch();
    },
    isRefetching: propertiesQuery.isRefetching || rentQuery.isRefetching,
  };
}
